import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  IconButton,
  Box,
  Checkbox,
  Backdrop,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "sonner";

// Treats null / undefined / whitespace-only strings as "no content".
const isBlank = (val) =>
  val === null || val === undefined || !String(val).trim();

const GenerateAIContentModal = ({
  open,
  onClose,
  onConfirm,
  apiUrl,
  headers,
  rows,
  initialAiContent,
  initialAiSuggestion,
}) => {
  const [aiMap, setAiMap] = useState({});
  const [aiSuggestionMap, setAiSuggestionMap] = useState({});
  const [selected, setSelected] = useState({});
  const [generating, setGenerating] = useState(false);
  const logo = "/loaderLogo.png";
  const [hasGenerated, setHasGenerated] = useState(false);
  const [currentContentMap, setCurrentContentMap] = useState({});

  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("Starting up");

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      const seeded = {};
      const seededSuggestion = {}; // NEW
      const seededSelected = {};
      const seededCurrent = {};
      rows.forEach((r) => {
        seeded[r.key] = (initialAiContent && initialAiContent[r.key]) || "";
        seededSuggestion[r.key] =
          (initialAiSuggestion && initialAiSuggestion[r.key]) || ""; // NEW
        seededSelected[r.key] = true;
        seededCurrent[r.key] = truncateToWordLimit(
          r.content || "",
          MAX_CURRENT_CONTENT_WORDS,
        );
      });
      setAiMap(seeded);
      setAiSuggestionMap(seededSuggestion);
      setSelected(seededSelected);
      setCurrentContentMap(seededCurrent);
      setHasGenerated(
        rows.some((r) => (initialAiContent && initialAiContent[r.key]) || ""),
      );
    }
  }, [open]);

  useEffect(() => {
    let interval;
    if (generating) {
      setProgress(0);
      setProgressLabel("Starting up");
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return prev;
          // Fast at first, slows down as it approaches 95%
          const increment =
            prev < 40 ? 6 : prev < 70 ? 3 : prev < 88 ? 1.2 : 0.4;
          const next = Math.min(prev + increment, 95);

          if (next < 30) setProgressLabel("Reading sections");
          else if (next < 55) setProgressLabel("Analyzing content");
          else if (next < 80) setProgressLabel("Calculating answers");
          else setProgressLabel("Almost there");

          return next;
        });
      }, 250);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [generating]);

  const filteredRows = rows.filter((r) =>
    String(r.label ?? "")
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase()),
  );

  // all selected rows (visible or hidden) - used for Generate
  const selectedRows = rows.filter((r) => selected[r.key]);

  // select-all checkbox only reflects the rows currently visible
  const visibleSelectedCount = filteredRows.filter(
    (r) => selected[r.key],
  ).length;
  const allSelected =
    filteredRows.length > 0 && visibleSelectedCount === filteredRows.length;
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  const handleToggleRow = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleSelectAll = () => {
    const nextValue = !allSelected;
    setSelected((prev) => {
      const next = { ...prev };
      filteredRows.forEach((r) => {
        next[r.key] = nextValue;
      });
      return next;
    });
  };

  const handleGenerate = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one section to generate.");
      return;
    }

    // Check whether any selected section has empty content
    const emptySelectedRows = selectedRows.filter((r) =>
      isBlank(currentContentMap[r.key]),
    );

    if (emptySelectedRows.length > 0) {
      toast.warning("Please select only sections with available content");
      return;
    }

    // Only sections with available content will reach here
    const rowsWithContent = selectedRows;

    const observations = rowsWithContent.map((r) =>
      String(currentContentMap[r.key]).trim(),
    );

    setGenerating(true);
    try {
      const response = await axios.post(
        `${apiUrl}question/askai`,
        { observations },
        { headers },
      );

      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      setAiMap((prev) => {
        const next = { ...prev };
        rowsWithContent.forEach((r, idx) => {
          const obs = results[idx]?.ai_observation;
          next[r.key] = isBlank(obs) ? "" : String(obs).trim();
        });
        return next;
      });

      setAiSuggestionMap((prev) => {
        const next = { ...prev };
        rowsWithContent.forEach((r, idx) => {
          const item = results[idx];
          // when no requirement was found, don't push the "not available" text into the field
          const sugg =
            item?.status === "not_available" ? "" : item?.ai_suggestion;
          next[r.key] = isBlank(sugg) ? "" : String(sugg).trim();
        });
        return next;
      });
      setHasGenerated(true);
      setProgress(100);
      setProgressLabel("Done");

      if (results.length !== rowsWithContent.length) {
        toast.error(
          "AI returned a different number of answers than sections sent - please review each field carefully before clicking Apply.",
        );
      } else if (results.every((r) => r?.status === "not_available")) {
        toast.info(
          "No applicable requirement was found in the reference documents for these entries.",
        );
      } else if (results.some((r) => r?.status === "needs_review")) {
        toast.warning(
          "Some suggestions need review. Please check them before clicking Apply.",
        );
      } else {
        toast.success(
          "AI content generated. Review it below, then click Apply.",
        );
      }
    } catch (error) {
      console.error("Error generating AI content:", error);

      if (error.response) {
        const status = error.response.status;
        const message =
          typeof error.response.data === "string" && error.response.data.trim()
            ? error.response.data
            : error.response.data?.message ||
              `Request failed with status ${status}.`;

        if (status === 401 || status === 403) {
          toast.error(
            "You're not authorized to use AI generation. Please sign in again.",
          );
        } else {
          toast.error(message);
        }
      } else if (error.request) {
        toast.error(
          "Could not reach the AI service. Please check your connection and try again.",
        );
      } else {
        toast.error("Failed to generate AI content. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const MAX_CURRENT_CONTENT_WORDS = 100;

  const countWords = (str) => {
    const trimmed = (str ?? "").trim();
    return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
  };

  const truncateToWordLimit = (str, limit) => {
    const words = (str ?? "").trim().split(/\s+/);
    if (words.length <= limit || (words.length === 1 && words[0] === "")) {
      return str ?? "";
    }
    return words.slice(0, limit).join(" ");
  };

  const handleCurrentContentChange = (key, value) => {
    const truncatedValue = truncateToWordLimit(
      value,
      MAX_CURRENT_CONTENT_WORDS,
    );
    setCurrentContentMap((prev) => ({ ...prev, [key]: truncatedValue }));
  };

  const handleFieldChange = (key, value) => {
    setAiMap((prev) => ({ ...prev, [key]: value ?? "" }));
  };

  const handleSuggestionChange = (key, value) => {
    setAiSuggestionMap((prev) => ({ ...prev, [key]: value ?? "" }));
  };

  const handleOk = () => {
    onConfirm(aiMap, currentContentMap, aiSuggestionMap);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={generating ? undefined : handleCancel}
      fullScreen
      disableEscapeKeyDown={generating}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon color="primary" />
          Generate Observation with AI
        </Box>

        <IconButton
          onClick={handleCancel}
          disabled={generating}
          aria-label="close"
          sx={{
            color: generating ? "action.disabled" : "error.main",
            "&:hover": {
              backgroundColor: generating ? "transparent" : "error.lighter",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ px: { xs: 1, sm: 2 }, overflowX: "hidden" }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search by section..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={generating}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm("")}
                  aria-label="clear search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflowX: "auto", maxWidth: "100%" }}
        >
          <Table sx={{ minWidth: { xs: 700, sm: 900 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell
                  sx={{
                    width: "6%",
                    textAlign: "center",
                    fontWeight: 600,
                    minWidth: 48,
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleToggleSelectAll}
                    disabled={generating}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: "13%",
                    fontWeight: 600,
                    color: "white",
                    minWidth: 100,
                  }}
                >
                  Section
                </TableCell>
                <TableCell
                  sx={{
                    width: "27%",
                    fontWeight: 600,
                    color: "white",
                    minWidth: 160,
                  }}
                >
                  Observation
                </TableCell>
                <TableCell
                  sx={{
                    width: "27%",
                    fontWeight: 600,
                    color: "white",
                    minWidth: 160,
                  }}
                >
                  AI Generated Observation
                </TableCell>
                <TableCell
                  sx={{
                    width: "28%",
                    fontWeight: 600,
                    color: "white",
                    minWidth: 160,
                  }}
                >
                  AI Generated Suggestion
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No sections found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((row) => {
                const rowHasContent = !isBlank(currentContentMap[row.key]);
                return (
                  <TableRow key={row.key}>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Checkbox
                        checked={!!selected[row.key]}
                        onChange={() => handleToggleRow(row.key)}
                        disabled={generating}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{row.label}</Typography>
                      {!rowHasContent && (
                        <Typography variant="caption" color="text.secondary">
                          No content to send
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <textarea
                        className="textarea-note"
                        rows={5}
                        value={currentContentMap[row.key] ?? ""}
                        onChange={(e) =>
                          handleCurrentContentChange(row.key, e.target.value)
                        }
                        disabled={generating}
                        style={{
                          width: "100%",
                          background: "#fafafa",
                          minWidth: 140,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          color:
                            countWords(currentContentMap[row.key]) >=
                            MAX_CURRENT_CONTENT_WORDS
                              ? "error.main"
                              : "text.secondary",
                        }}
                      >
                        {countWords(currentContentMap[row.key])}/
                        {MAX_CURRENT_CONTENT_WORDS} words
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <textarea
                        className="textarea-note"
                        rows={5}
                        value={aiMap[row.key] || ""}
                        placeholder="AI generated content will appear here"
                        onChange={(e) =>
                          handleFieldChange(row.key, e.target.value)
                        }
                        disabled={generating}
                        style={{ width: "100%", minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <textarea
                        className="textarea-note"
                        rows={5}
                        value={aiSuggestionMap[row.key] || ""}
                        placeholder="AI generated suggestion will appear here"
                        onChange={(e) =>
                          handleSuggestionChange(row.key, e.target.value)
                        }
                        disabled={generating}
                        style={{ width: "100%", minWidth: 140 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 1, sm: 2 },
          py: 1.5,
          justifyContent: { xs: "center", sm: "flex-end" },
        }}
      >
        <Button
          variant="contained"
          color="info"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerate}
          disabled={generating || selectedRows.length === 0}
          size="small"
          sx={{ minWidth: { xs: "100%", sm: "auto" } }}
        >
          {hasGenerated ? "Regenerate" : "Generate"} ({selectedRows.length})
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          onClick={handleOk}
          disabled={generating}
          size="small"
          sx={{ minWidth: { xs: "100%", sm: "auto" } }}
        >
          Apply
        </Button>

        <Button
          variant="contained"
          color="error"
          startIcon={<CloseIcon />}
          onClick={handleCancel}
          disabled={generating}
          size="small"
          sx={{ minWidth: { xs: "100%", sm: "auto" } }}
        >
          Cancel
        </Button>
      </DialogActions>

      {/* Full-screen centered loading overlay while AI is generating */}
      <Backdrop
        open={generating}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2.5,
          backgroundColor: "rgba(10, 10, 20, 0.75)",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 90,
            height: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Soft glow pulse behind logo */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(129,140,248,0.55) 0%, rgba(129,140,248,0) 70%)",
              animation: "aiGlowPulse 1.6s ease-in-out infinite",
            }}
          />

          {/* Rotating ring */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: "2px solid transparent",
              borderTopColor: "#818cf8",
              borderRightColor: "rgba(129,140,248,0.3)",
              animation: "aiSpin 1.1s linear infinite",
            }}
          />

          {/* Your Logo */}
          <img
            src={logo}
            alt="Loading"
            style={{
              width: 55,
              height: 55,
              objectFit: "contain",
              zIndex: 1,
              filter: "drop-shadow(0 0 8px rgba(129,140,248,0.9))",
            }}
          />
        </Box>

        <Box sx={{ textAlign: "center", width: 260 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#fff", fontWeight: 600, letterSpacing: 0.3, mb: 1 }}
          >
            {progressLabel}...
          </Typography>

          <Box
            sx={{
              width: "100%",
              height: 8,
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.15)",
              overflow: "hidden",
              mb: 0.75,
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                borderRadius: 4,
                background: "linear-gradient(90deg, #818cf8, #c7d2fe)",
                transition: "width 0.25s ease-out",
              }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 600 }}
          >
            {Math.round(progress)}%
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "rgba(255,255,255,0.5)", mt: 0.25 }}
          >
            This may take a few seconds
          </Typography>
        </Box>

        <Box
          component="style"
          dangerouslySetInnerHTML={{
            __html: `
              @keyframes aiSpin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              @keyframes aiGlowPulse {
                0%, 100% { transform: scale(0.85); opacity: 0.6; }
                50% { transform: scale(1.15); opacity: 1; }
              }
              @keyframes aiTwinkle {
                0%, 100% { transform: scale(0.85) rotate(0deg); opacity: 0.7; }
                50% { transform: scale(1.15) rotate(12deg); opacity: 1; }
              }
              @keyframes aiDotBounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                40% { transform: translateY(-4px); opacity: 1; }
              }
            `,
          }}
        />
      </Backdrop>
    </Dialog>
  );
};

export default GenerateAIContentModal;
